import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import PurchaserClient from "../PurchaserClient";
import type { CreateRejectionsParams } from "../Purchaser/Rejections";
import { queryClient, type QueryInvalidateOption } from "./client";

const FIVE_SECONDS = 5 * 1000;

/**
 * Rejections Query Options
 */
export const RejectionsQueryOptions = {
    RFQRejections(rfqPublicId?: string | null) {
        return queryOptions({
            queryKey: ["rfqRejections", rfqPublicId],
            queryFn: async () => {
                if (!rfqPublicId) throw new Error("Invalid RFQ Public ID");
                const response = await PurchaserClient.getRFQRejections(rfqPublicId);
                return response.data;
            },
            refetchInterval: FIVE_SECONDS,
            staleTime: FIVE_SECONDS,
            enabled: !!rfqPublicId,
        });
    },
    MailboxRejection(mailboxId?: number | null) {
        return queryOptions({
            queryKey: ["mailboxRejection", mailboxId],
            queryFn: async () => {
                if (!mailboxId) throw new Error("Invalid Mailbox ID");
                return await PurchaserClient.getMailboxRejection(mailboxId);
            },
            staleTime: FIVE_SECONDS,
            enabled: !!mailboxId,
        });
    },
};

/**
 * Rejections Hooks
 */
export const useRFQRejections = (rfqPublicId?: string | null) => {
    const query = useQuery(RejectionsQueryOptions.RFQRejections(rfqPublicId));
    const { data, ...rest } = query;

    const rejections = useMemo(() => data ?? [], [data]);

    return { rejections, ...rest };
};

export const useMailboxRejection = (mailboxId?: number | null) => {
    const query = useQuery(RejectionsQueryOptions.MailboxRejection(mailboxId));
    const { data, ...rest } = query;

    return { rejection: data, ...rest };
};

/**
 * Rejections Invalidation Functions
 */
export async function invalidateRFQRejectionsQueries(params?: { rfqPublicId?: string; option?: QueryInvalidateOption }) {
    const { rfqPublicId, option } = params ?? {};

    const queryKey: string[] = ["rfqRejections"];
    if (rfqPublicId) queryKey.push(rfqPublicId);

    if (option === "Remove") {
        await queryClient.removeQueries({ queryKey });
    } else {
        await queryClient.invalidateQueries({ queryKey });
    }
}

export async function invalidateMailboxRejectionQuery(mailboxId: number) {
    const queryKey = ["mailboxRejection", mailboxId];
    await queryClient.invalidateQueries({ queryKey });
}

/**
 * Rejections Mutations
 */
export const useCreateRejections = () => {
    const mutation = useMutation({
        mutationFn: async (params: CreateRejectionsParams) => {
            return await PurchaserClient.createRejections(params);
        },
        async onSuccess(_, variables) {
            await invalidateRFQRejectionsQueries({ rfqPublicId: variables.rfqPublicId });
            // Also invalidate individual mailbox rejection queries
            for (const mailboxId of variables.mailboxIds) {
                await invalidateMailboxRejectionQuery(mailboxId);
            }
        },
        onError(e) {
            console.error("Error creating rejections", e);
        },
    });

    return {
        createRejections: mutation.mutate,
        createRejectionsAsync: mutation.mutateAsync,
        isCreatingRejections: mutation.isPending,
    };
};

export const useDeleteRejection = () => {
    const mutation = useMutation({
        mutationFn: async (params: { rejectionId: number; rfqPublicId: string; mailboxId: number }) => {
            await PurchaserClient.deleteRejection(params.rejectionId);
            return params;
        },
        async onSuccess(params) {
            await invalidateRFQRejectionsQueries({ rfqPublicId: params.rfqPublicId });
            await invalidateMailboxRejectionQuery(params.mailboxId);
        },
        onError(e) {
            console.error("Error deleting rejection", e);
        },
    });

    return {
        deleteRejection: mutation.mutate,
        isDeletingRejection: mutation.isPending,
    };
};
